import AddBlock from '@clearkit/icons/glyphs/AddBlock';
import DirectionForward from '@clearkit/icons/glyphs/DirectionForward';
import Trash from '@clearkit/icons/glyphs/Trash';
import Unknown from '@clearkit/icons/glyphs/Unknown';
import {
  CKAttributeSelector,
  CKBox,
  CKButton,
  CKSwitch,
  CKTooltip,
} from 'clearkit';
import capitalize from 'lodash/capitalize';

import {
  attributeForFinder,
  backendSupportedDataType,
  findAttribute,
  readableType,
  sourceIcon,
} from '~lib/dataEnrichment';
import { isTypeMismatch } from '~lib/isTypeMismatch';

import ServiceIcon from '../ServiceIcon';

export default function FieldMappingGroup({
  group,
  type,
  objectType,
  onChange,
  onGroupChange,
  onRemove,
  onRemoveMapping,
  sourceAttributes,
  targetAttributes,
  addMapping,
  showError,
  errors,
}) {
  const inErrorState = (attribute) => {
    return !!errors[attribute] || (!attribute && showError);
  };

  return (
    <CKBox
      className="flex flex-col items-start p-4 -mx-4 grid"
      variant={CKBox.Variant['tinted-frame']}
    >
      <div className={'mapping-group pb-2'}>
        <div className={'flex flex-row content-center'}>
          <p className="text-base font-medium text-gray-600">Address Group</p>
          <CKTooltip
            placement="top"
            tooltip="
                Address groups allow multiple mappings to respect the same
                overwrite setting. This is most often used for linking multiple
                address components (i.e. state and country) to ensure they
                always match."
          >
            <div className="flex pt-1 ml-2 text-gray-300 outline-none">
              <Unknown height="16" width="16" />
            </div>
          </CKTooltip>
        </div>
        <div className="pl-4">
          <CKSwitch
            checked={!group.conditionalOverwrite}
            id={`mappings[${group.groupId}].overwrite`}
            onChange={() =>
              onGroupChange({
                conditionalOverwrite: !group.conditionalOverwrite,
              })
            }
            srOnly={false}
          >
            {group.conditionalOverwrite ? 'Disabled' : 'Enabled'}
          </CKSwitch>
        </div>
        <div className="text-right">
          <CKButton isSmall onClick={onRemove} variant="tertiary">
            <Trash className="text-gray-600" />
          </CKButton>
        </div>
      </div>
      <hr />
      <div className="w-full mt-4">
        <div className="space-y-6">
          {group.fieldMappings.map((mapping) => {
            const selectedSourceAttribute = sourceAttributes
              ? findAttribute(
                  attributeForFinder(mapping, 'source'),
                  sourceAttributes,
                )
              : null;
            const selectedTargetAttribute = targetAttributes
              ? findAttribute(
                  {
                    value: mapping.targetField,
                    objectType: objectType.name,
                    sourceType: type,
                  },
                  targetAttributes,
                )
              : null;

            const rowKey = `${mapping.targetField ||
              'empty'}:${mapping.sourceField || 'empty'}-${mapping.index}`;

            return (
              <div className="mapping-group-row" key={rowKey}>
                <CKAttributeSelector
                  attributes={sourceAttributes}
                  icon={<ServiceIcon type={selectedSourceAttribute} />}
                  isTypeMismatch={(attribute) =>
                    isTypeMismatch(attribute, selectedTargetAttribute)
                  }
                  onSelectedItemChange={(selected) => {
                    onChange(mapping.index)({
                      sourceField: selected?.value,
                      sourceObjectType: selected?.meta?.objectType,
                      sourceType: selected?.meta?.sourceType,
                    });
                  }}
                  placeholder="Find an attribute"
                  selectedAttribute={selectedSourceAttribute || {}}
                  variant={inErrorState(mapping.sourceField) && 'error'}
                  zIndex={30}
                />
                <DirectionForward className="text-gray" />
                <CKAttributeSelector
                  attributes={targetAttributes}
                  icon={sourceIcon(type)}
                  isTypeMismatch={(attribute) =>
                    isTypeMismatch(selectedSourceAttribute, attribute)
                  }
                  onSelectedItemChange={(selected) =>
                    onChange(mapping.index)({
                      targetField: selected?.value,
                      dataType: backendSupportedDataType(
                        selected?.meta?.dataType,
                      ),
                    })
                  }
                  placeholder={`Choose a ${readableType(type)} ${capitalize(
                    objectType.name,
                  )} attribute`}
                  selectedAttribute={selectedTargetAttribute || {}}
                  variant={inErrorState(mapping.targetField) && 'error'}
                  zIndex={30}
                />
                <div>&nbsp;</div>
                <div className="pt-1 text-right">
                  <CKButton
                    isSmall
                    onClick={onRemoveMapping(mapping.index)}
                    variant="tertiary"
                  >
                    <Trash className="text-gray-600" />
                  </CKButton>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <CKButton
        className="mt-4 justify-self-start"
        isSmall
        leftIcon={<AddBlock />}
        onClick={() =>
          addMapping({
            groupId: group.groupId,
            conditionalOverwrite: group.conditionalOverwrite,
          })
        }
        variant="tertiary"
        variantColor="blue"
      >
        Add mapping to address group
      </CKButton>
    </CKBox>
  );
}
